import { useQueryClient } from '@tanstack/react-query';
import type { Team } from 'api-client';
import { BillingModeEnum, OrgsApi } from 'api-client';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import { useEnsureUserTimezone } from '@/hooks/useEnsureUserTimezone';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useTabTitleNotification } from '@/hooks/useTabTitleNotification';
import { isAccessPausedPath } from '@/lib/accessPaused';
import { useAgentFeedWebSocket } from '@/lib/agent-feed/realtime/useAgentFeedWebSocket';
import { isBillingRequiredPath } from '@/lib/billingRequired';
import * as bootMetrics from '@/lib/boot/bootMetrics';
import {
  fetchAvailableOrgs,
  takeBootOrgsRequest,
} from '@/lib/boot/bootRequests';
import { SingleObjectDeleteJobHost } from '@/lib/crm/components/SingleObjectDeleteJobHost';
import { useBulkActionRecentToasts } from '@/lib/crm/hooks/useBulkActionRecentToasts';
import { useCRMEventPolling } from '@/lib/crm/hooks/useCRMEventPolling';
import { resetStaleMappingRecoveries } from '@/lib/crm/hooks/usePersonByEmail';
import { crmQueryKeys, isCrmListKey } from '@/lib/crm/query/queries';
import { CrmSubscriptionProvider } from '@/lib/crm/realtime/CrmSubscriptionProvider';
import { useCrmWebSocket } from '@/lib/crm/realtime/useCrmWebSocket';
import {
  clearAll as clearCrmObjectStore,
  subscribeToEviction,
} from '@/lib/crm/store/crmObjectStore';
import { invalidateListSentinelsForEvictedIds } from '@/lib/crm/store/evictionCoupling';
import { ApiError, isTransientError, NetworkError } from '@/lib/errors';
import { updateMultiOrgFlag } from '@/lib/localStorage/multiOrgFlag';
import {
  computeInitialHydration,
  type InitialHydration,
  selectInitialCurrentOrg,
} from '@/lib/localStorage/orgContextHydration';
import { setPersistedAvailableOrgs } from '@/lib/localStorage/persistedAvailableOrgs';
import { logger } from '@/lib/logger';
import type { ProposalsListResponse } from '@/lib/proposals/core/types';
import { useMyPendingProposals } from '@/lib/proposals/hooks/useProposals';
import { useProposalWebSocket } from '@/lib/proposals/realtime/useProposalWebSocket';

import { useAuthInfo, useUser } from '../allauth_auth/hooks.js';
import { getApiConfiguration } from '../api/utils';
import { setOrgContext } from '../lib/mixpanel';

interface OrgContextType {
  // currentOrg is the full Team payload from loadTeams(). It includes
  // permissionGroups, permissions, and features — all three arrive
  // synchronously on the same payload (no secondary fetch needed).
  // currentOrg.features is undefined on stale payloads: useFeatureAccess
  // treats that as all features present so a stale payload never paywalls.
  currentOrg: Team | null;
  availableOrgs: Team[];
  isLoading: boolean;
  error: string | null;
  hasLoadedSuccessfully: boolean;
  setCurrentTeam: (team: Team) => void;
  switchOrg: (orgId: number) => Promise<void>;
  refreshCurrentOrg: () => Promise<void>;
  /**
   * Atomically patch a subset of fields on the org identified by `orgId`,
   * across BOTH `currentOrg` (if it is the active org) and the cached
   * `availableOrgs` entry. Uses functional state updates so two concurrent
   * single-field saves (e.g. currency + time format) merge instead of one
   * clobbering the other, and so a later `switchOrg` reads the updated
   * `availableOrgs` record rather than a stale pre-save snapshot.
   */
  updateOrgFields: (orgId: number, fields: Partial<Team>) => void;
}

// Exported so test and storybook providers can supply a mock value without
// pulling in the full TeamProvider (which would fire real auth + WebSocket
// + Mixpanel side effects). Production code should still go through
// TeamProvider + useOrg().
export const OrgContext = createContext<OrgContextType | undefined>(undefined);

interface TeamProviderProps {
  children: ReactNode;
}

export function TeamProvider({ children }: TeamProviderProps) {
  const { isAuthenticated } = useAuthInfo();
  const user = useUser();
  // Populate CustomUser.timezone from the browser on first authenticated
  // load when the field is blank (Issue #11160). Set-if-blank, once per
  // session — never overwrites a non-empty value.
  useEnsureUserTimezone(isAuthenticated === true);
  const queryClient = useQueryClient();
  // Hydration: compute once per provider lifetime. Pure / idempotent —
  // safe under React 19 StrictMode, which may invoke the function body
  // twice during dev. Side effect (bootMetrics.mark) is deferred to a
  // mount-only effect below.
  const hydrationRef = useRef<InitialHydration | null>(null);
  if (hydrationRef.current === null) {
    hydrationRef.current = computeInitialHydration(user?.id);
  }
  const initialHydration = hydrationRef.current;
  const [currentOrg, setCurrentTeamInternal] = useState<Team | null>(
    initialHydration.currentOrg
  );
  // Ref to track current org for use in callbacks that might have stale closures
  const currentOrgRef = useRef<Team | null>(initialHydration.currentOrg);

  const setCurrentTeam = useCallback((team: Team | null) => {
    currentOrgRef.current = team;
    setCurrentTeamInternal(team);
  }, []);

  // Single source of truth for the `currentOrgRef` mirror. `setCurrentTeam`
  // sets the ref synchronously for callers that read it within the same tick
  // (e.g. loadTeams' stale-closure workaround), and this effect re-affirms it
  // whenever `currentOrg` commits — including functional updates from
  // `updateOrgFields`, whose next value isn't known at call time. Keeping ref
  // synchronization OUT of the state updater means an org switch racing with a
  // settings save can't leave the ref pointing at the pre-save org.
  useEffect(() => {
    currentOrgRef.current = currentOrg;
  }, [currentOrg]);

  const [availableOrgs, setAvailableTeams] = useState<Team[]>(
    initialHydration.orgs
  );

  const updateOrgFields = useCallback(
    (orgId: number, fields: Partial<Team>) => {
      // Functional updates so concurrent single-field saves merge against the
      // freshest state instead of clobbering each other. Guarded by id so a
      // completion for an org the user has since switched away from does not
      // overwrite the now-active org.
      setCurrentTeamInternal(prev => {
        if (!prev || prev.id !== orgId) {
          return prev;
        }
        // Keep the updater pure — the `currentOrgRef` mirror is synchronized by
        // the committed-`currentOrg` effect above, so a concurrent org switch
        // can't leave the ref pointing at the pre-save org.
        return { ...prev, ...fields };
      });
      // Keep the cached org list in sync so switching orgs and back reflects
      // the save rather than the stale pre-save value.
      setAvailableTeams(prev =>
        prev.map(team => (team.id === orgId ? { ...team, ...fields } : team))
      );
    },
    []
  );

  // True when the current org requires a Stripe subscription and has none.
  // Used to suppress org-scoped hooks on /{orgId}/settings/billing so they
  // don't fire 403-doomed requests. On /{orgId}/billing-required itself,
  // currentOrg is null (cleared by the loadTeams guard below), so the
  // existing !!currentOrg?.id gate already disables these hooks.
  const isBillingGated = useMemo(
    () =>
      currentOrg?.billingMode === BillingModeEnum.Stripe &&
      !currentOrg?.hasActiveSubscription &&
      !currentOrg?.pendingInviteCheckout,
    [
      currentOrg?.billingMode,
      currentOrg?.hasActiveSubscription,
      currentOrg?.pendingInviteCheckout,
    ]
  );

  // Seat feature for the AI Actions surface. Same semantics as
  // useFeatureAccess (which cannot be called here — it reads this context):
  // a missing `features` key is a stale payload and never paywalls, an
  // empty list is a restricted seat. Every proposals read below is gated on
  // it so a viewer's session never issues requests the server 403s.
  const orgFeatures = currentOrg?.features;
  const hasAiActionsFeature =
    orgFeatures === undefined || orgFeatures.includes('ai_actions');

  // Proposal WebSocket for real-time proposal lifecycle events
  // (proposal.created). Server-side filters by
  // assignee so only the assignee's open tabs receive each push.
  useProposalWebSocket({
    orgId: currentOrg?.id ?? null,
    enabled: !!currentOrg?.id && !isBillingGated && hasAiActionsFeature,
  });

  // Subscribe to cached pending-proposals list (shares cache with ProposalListPanel — no extra request).
  // Unread = proposals where read_at IS NULL. Drives the (N) prefix on document.title when tab is hidden.
  const { data: pendingProposalsForTitle } = useMyPendingProposals(
    currentOrg?.id ?? 0,
    { enabled: !!currentOrg?.id && !isBillingGated && hasAiActionsFeature }
  ) as { data: ProposalsListResponse | undefined };

  const unreadCount = useMemo(() => {
    const results = pendingProposalsForTitle?.results;
    if (!results) {
      return 0;
    }
    return results.filter(p => p.read_at === null).length;
  }, [pendingProposalsForTitle?.results]);

  useTabTitleNotification(unreadCount);

  // Agent feed WebSocket for real-time background-agent lifecycle events
  // (trigger.pending / agent.started / agent.completed). Server-side filters
  // by org access so only authorized users receive pushes.
  // Gated by agent_feed feature flag to avoid unnecessary WS connections.
  // NOTE: useFeatureFlag calls useOrg() which IS this context — use
  // useFeatureFlags directly to avoid circular dependency.
  const { data: featureFlags } = useFeatureFlags(currentOrg?.id, {
    enabled: !isBillingGated,
  });
  const agentFeedEnabled = featureFlags?.includes('agent_feed') ?? false;
  useAgentFeedWebSocket({
    orgId: currentOrg?.id ?? null,
    enabled:
      !!currentOrg?.id &&
      agentFeedEnabled &&
      !isBillingGated &&
      hasAiActionsFeature,
  });

  // Recent bulk-action job toast notifications — fires once per org
  // per session to surface completions that finished while the user
  // was away.
  useBulkActionRecentToasts(isBillingGated ? null : (currentOrg?.id ?? null));

  const [isLoading, setIsLoading] = useState(false); // Start false - we'll set true when actually loading
  const [error, setError] = useState<string | null>(null);
  const loadAttemptedRef = useRef(false);
  const hasLoadedSuccessfullyRef = useRef(initialHydration.source === 'cache');
  // Mirror of hasLoadedSuccessfullyRef as state so consumers can react to it.
  // Required so callers can distinguish "haven't loaded yet" from
  // "loaded and the user genuinely has no orgs", instead of inferring from
  // availableOrgs.length (which conflates both).
  const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(
    initialHydration.source === 'cache'
  );
  // Guard against concurrent loadTeams calls (speculative + auth retry racing)
  const isLoadingRef = useRef(false);
  // Latest known user.id, kept in a ref so loadTeams can compare the user
  // at request-issuance time against the user at response-handling time.
  // Required to discard responses from a session that no longer matches the
  // current user (e.g. logout/login race) instead of writing them under the
  // previous user's cache key.
  const userIdRef = useRef<number | undefined>(user?.id);
  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);
  const [failedWithAuthError, setFailedWithAuthError] = useState(false);
  // Exponential backoff for auth retries to prevent infinite loops
  const authRetryCountRef = useRef(0);
  const authRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_AUTH_RETRIES = 5;
  // Ref to track if component is still mounted, used to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track previous org ID for scoped cache clearing on org switch
  const previousOrgIdRef = useRef<number | null>(null);

  const loadTeams = useCallback(async () => {
    // Prevent concurrent calls (speculative load + auth retry can race)
    if (isLoadingRef.current) {
      logger.debug('OrgContext: loadTeams already in flight, skipping');
      return;
    }
    isLoadingRef.current = true;
    // Snapshot user.id at request issuance so we can detect a mid-flight
    // user change. The API response reflects whoever's auth cookies are
    // current at response time, which may differ from the user we issued
    // the request for if the user logged out/in mid-flight.
    const requestUserId = userIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      logger.debug('OrgContext: Loading teams...');

      // Create fresh API instance each time to get latest auth state
      const apiConfig = getApiConfiguration();
      logger.debug('OrgContext: API config', {
        basePath: apiConfig.basePath,
        hasCSRF: !!apiConfig.headers?.['X-CSRFToken'],
        credentials: apiConfig.credentials,
      });
      const api = new OrgsApi(apiConfig);
      // On a hard load prefetch.ts started this request at parse time, before
      // React mounted — adopt it rather than issue a second one. Any later
      // load (retry, org switch) finds none pending and fetches for itself.
      const teams = await (takeBootOrgsRequest() ?? fetchAvailableOrgs(api));

      // Skip state updates if component unmounted
      if (!isMountedRef.current) {
        return;
      }

      // Discard the response if the user changed mid-flight. Writing the
      // response would either pollute the previous user's cache with the
      // current user's data, or vice versa.
      if (requestUserId !== userIdRef.current) {
        logger.debug(
          'OrgContext: User changed mid-flight, discarding teams response',
          { requestUserId, currentUserId: userIdRef.current }
        );
        return;
      }

      logger.debug('OrgContext: Teams found', {
        count: teams.length,
        teams: teams.map(t => ({ id: t.id, name: t.name })),
      });
      setAvailableTeams(teams);
      if (requestUserId !== undefined) {
        queryClient.setQueryData(['availableOrgs', requestUserId], teams);
        setPersistedAvailableOrgs(requestUserId, teams);
      }
      hasLoadedSuccessfullyRef.current = true;
      setHasLoadedSuccessfully(true);
      authRetryCountRef.current = 0;

      // Auto-select team logic
      if (
        isAccessPausedPath(window.location.pathname) ||
        isBillingRequiredPath(window.location.pathname)
      ) {
        // On the access-paused or billing-required route, never hold an org as
        // current — keep org-scoped hooks gated off so we don't poll a
        // deactivated or billing-gated org. Both pages read orgId from URL params.
        if (currentOrgRef.current) {
          logger.debug(
            'OrgContext: on access-paused or billing-required route, clearing current org'
          );
          setCurrentTeam(null);
        }
      } else if (teams.length === 1) {
        logger.debug('OrgContext: Auto-selecting single team', {
          name: teams[0].name,
        });
        // If user has only one team, auto-select it
        setCurrentTeam(teams[0]);
      } else if (teams.length > 1) {
        // For multiple teams, only reset to team selector if no team is currently selected.
        // If a team is already selected (e.g., from URL via TeamGuard), preserve it to avoid
        // unmounting the current page and causing double renders/animations.
        // Use ref to get current value (loadTeams closure may have stale state).
        if (!currentOrgRef.current) {
          logger.debug(
            'OrgContext: Multiple teams found, showing team selector'
          );
          setCurrentTeam(selectInitialCurrentOrg(teams));
        } else {
          // Update currentOrg with fresh data from API response (preserves team selection
          // while ensuring member lists, roles, etc. reflect latest state).
          const currentOrg = currentOrgRef.current;
          const freshTeam = teams.find(t => t.id === currentOrg.id);
          if (freshTeam) {
            logger.debug(
              'OrgContext: Multiple teams found, refreshing current selection',
              { orgId: freshTeam.id }
            );
            setCurrentTeam(freshTeam);
          } else {
            logger.warn(
              'OrgContext: Current team not found in fresh API response',
              {
                orgId: currentOrg.id,
                availableTeamIds: teams.map(t => t.id),
                component: 'org-context',
              }
            );
          }
        }
      } else {
        logger.debug('OrgContext: No teams found');
      }
      // If no teams, currentOrg remains null (user needs to create or be invited to a team)

      // Success - always set loading false
      setIsLoading(false);
    } catch (err: unknown) {
      // Check if still mounted before updating state
      if (!isMountedRef.current) {
        return;
      }

      logger.error(
        'Failed to load teams',
        err instanceof Error ? err : undefined,
        { component: 'OrgContext' }
      );

      // Skip state updates if component unmounted
      if (!isMountedRef.current) {
        return;
      }

      // 401/403 = not authenticated (speculative load failed), don't show error
      // Will be retried when auth completes
      const status = (err as any)?.response?.status; // eslint-disable-line @typescript-eslint/no-explicit-any -- Axios error response structure requires runtime type guard
      if (status === 401 || status === 403) {
        logger.debug('OrgContext: Not authenticated, will retry after auth');
        setFailedWithAuthError(true);
        setIsLoading(false);
        return;
      }

      // Reuse `status` extracted above for the 401/403 check. Normalize the
      // generated-client error into a typed error so isTransientError can classify
      // it: a numeric status means the server responded (ApiError → 5xx is
      // transient); no status means the request never reached the server
      // (NetworkError → transient).
      const normalizedError =
        typeof status === 'number'
          ? new ApiError('Failed to load teams', status)
          : new NetworkError();
      if (isTransientError(normalizedError)) {
        logger.debug(
          'OrgContext: transient teams load failure — keeping prior state, will retry',
          { component: 'OrgContext' }
        );
      } else {
        setError("We couldn't load your teams. Try again.");
      }
      setIsLoading(false);
    } finally {
      isLoadingRef.current = false;
    }
  }, [setCurrentTeam, queryClient]);

  const switchOrg = useCallback(
    async (orgId: number) => {
      const team = availableOrgs.find(t => t.id === orgId);
      if (team) {
        previousOrgIdRef.current = orgId;
        setCurrentTeam(team);
      } else {
        throw new Error(`Team with ID "${orgId}" not found`);
      }
    },
    [availableOrgs, setCurrentTeam]
  );

  const refreshCurrentOrg = useCallback(async () => {
    await loadTeams();
  }, [loadTeams]);

  // Fire-and-forget wrapper for the WebSocket hook: a permission-affecting
  // event (role / permission-group change, role membership) means
  // `currentOrg.permissions` is stale, and loadTeams() is the only thing that
  // refreshes it. loadTeams owns its own error handling.
  const handlePermissionsChanged = useCallback(() => {
    void loadTeams();
  }, [loadTeams]);

  // CRM WebSocket for real-time event streaming (primary mechanism).
  // Returns lastEventIdRef (a ref, not state) so the poller can read
  // the latest WS event ID synchronously without waiting for a render.
  // Always enabled when an org is loaded. Mounted after loadTeams so the
  // permissions-changed handoff can be a plain stable callback.
  const {
    lastEventIdRef: wsLastEventIdRef,
    status: wsStatus,
    subscribe: wsSubscribe,
    unsubscribe: wsUnsubscribe,
    forceReconnect: wsForceReconnect,
  } = useCrmWebSocket({
    orgId: currentOrg?.id ?? null,
    enabled: !!currentOrg?.id && !isBillingGated,
    onPermissionsChanged: handlePermissionsChanged,
  });

  // CRM event polling for cache invalidation (fallback / safety net).
  // Reads wsLastEventIdRef directly — always has the latest value from
  // the WS event handler, no React render cycle in between.
  // Passes wsStatus to prevent stale cache when WS is disconnected, and
  // wsForceReconnect so the poller can escalate a half-open ("zombie") WS
  // connection into a reconnect.
  useCRMEventPolling({
    orgId: currentOrg?.id ?? null,
    enabled: !!currentOrg?.id && !isBillingGated,
    wsLastEventIdRef,
    wsStatus,
    forceReconnect: wsForceReconnect,
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (authRetryTimerRef.current) {
        clearTimeout(authRetryTimerRef.current);
      }
    };
  }, []);

  // Speculative load on mount - starts in parallel with auth
  // Also handles cleanup on unmount to prevent state updates after unmount
  useEffect(() => {
    // Ensure mounted flag is set (may have been cleared by cleanup when loadTeams identity changed)
    isMountedRef.current = true;

    if (!loadAttemptedRef.current) {
      loadAttemptedRef.current = true;
      logger.debug('OrgContext: Starting speculative teams load...');
      loadTeams();
    }

    // Cleanup: mark as unmounted to prevent state updates after unmount
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: cleanup must only run on real unmount, not when loadTeams identity changes
  }, []);

  // Emit hydration telemetry once per mount. mark() always records to the
  // internal marks array (consumed by summary() for the production Sentry
  // boot.prefetch breadcrumb); only the verbose [boot] console log is gated
  // behind BOOT_DEBUG. Production overhead is minimal but non-zero.
  useEffect(() => {
    bootMetrics.mark('org-hydrate', { source: initialHydration.source });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only telemetry; initialHydration is stable for the lifetime of this provider
  }, []);

  // Handle auth state changes - retry load when auth is confirmed
  // Uses exponential backoff to prevent infinite retry loops on persistent 403s
  // NOTE: failedWithAuthError is intentionally NOT in dependencies.
  // We READ it to decide whether to retry, but changing it should NOT trigger the effect.
  // Including it would cause a loop: loadTeams() sets it → effect runs → loadTeams() sets it → loop
  useEffect(() => {
    if (isAuthenticated === true) {
      // Auth confirmed - retry load only if:
      // 1. We haven't loaded successfully yet OR had an auth error
      // 2. Not currently loading
      // 3. No non-auth error occurred (to prevent infinite retry on 500 errors)
      // 4. Haven't exceeded max auth retries
      const shouldRetry =
        (!hasLoadedSuccessfullyRef.current || failedWithAuthError) &&
        !isLoading &&
        !error &&
        authRetryCountRef.current < MAX_AUTH_RETRIES;
      if (shouldRetry) {
        const attempt = authRetryCountRef.current;
        const delayMs =
          attempt === 0 ? 0 : Math.min(1000 * 2 ** (attempt - 1), 16000);
        logger.debug('OrgContext: User authenticated, retrying teams load', {
          attempt: attempt + 1,
          maxRetries: MAX_AUTH_RETRIES,
          delayMs,
        });
        authRetryCountRef.current = attempt + 1;

        // Clear any pending retry timer
        if (authRetryTimerRef.current) {
          clearTimeout(authRetryTimerRef.current);
        }

        if (delayMs === 0) {
          setFailedWithAuthError(false);
          loadTeams();
        } else {
          authRetryTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              setFailedWithAuthError(false);
              loadTeams();
            }
          }, delayMs);
        }
      } else if (
        authRetryCountRef.current >= MAX_AUTH_RETRIES &&
        failedWithAuthError
      ) {
        // Exhausted retries — surface the error so the UI can show it
        logger.error(
          'Auth retries exhausted',
          new Error('Failed to load teams after max retries'),
          { component: 'OrgContext', maxRetries: MAX_AUTH_RETRIES }
        );
        setFailedWithAuthError(false);
        setError(
          "We couldn't load your teams. Refresh the page and try again."
        );
      }
    } else if (isAuthenticated === false) {
      // User logged out - reset state
      logger.debug('OrgContext: User not authenticated, resetting state');
      if (authRetryTimerRef.current) {
        clearTimeout(authRetryTimerRef.current);
        authRetryTimerRef.current = null;
      }
      setCurrentTeam(null);
      setAvailableTeams([]);
      setError(null);
      setIsLoading(false);
      hasLoadedSuccessfullyRef.current = false;
      setHasLoadedSuccessfully(false);
      setFailedWithAuthError(false);
      authRetryCountRef.current = 0;
    }
    // isAuthenticated === undefined means still loading auth, do nothing
    // eslint-disable-next-line react-hooks/exhaustive-deps -- failedWithAuthError intentionally excluded to prevent infinite loop
  }, [isAuthenticated, isLoading, error, loadTeams]);

  // Debug: Log loading state changes
  useEffect(() => {
    logger.debug('OrgContext: Loading state changed', {
      isLoading,
      error,
      teamsCount: availableOrgs.length,
    });
  }, [isLoading, error, availableOrgs.length]);

  // Track last org id to prevent duplicate Mixpanel calls
  const lastOrgIdRef = useRef<number | null>(null);

  // Update Mixpanel org context when currentOrg changes
  useEffect(() => {
    const newOrgId = currentOrg?.id ?? null;
    if (newOrgId !== lastOrgIdRef.current) {
      lastOrgIdRef.current = newOrgId;
      // Track previousOrgId for cache clearing (only if org actually changed via switchOrg)
      // Auto-selection during initial load shouldn't set previousOrgId
      if (newOrgId !== null && previousOrgIdRef.current === null) {
        previousOrgIdRef.current = newOrgId;
      }
      if (currentOrg?.id && currentOrg?.name) {
        setOrgContext({ id: currentOrg.id, name: currentOrg.name });
      } else {
        setOrgContext(null);
      }
    }
  }, [currentOrg?.id, currentOrg?.name]);

  // Clear CRM object store when org changes (prevents cross-org data)
  const lastCrmOrgIdRef = useRef<number | null>(null);
  useEffect(() => {
    const newOrgId = currentOrg?.id ?? null;
    if (newOrgId !== lastCrmOrgIdRef.current) {
      if (lastCrmOrgIdRef.current !== null) {
        // Only flush when switching away from an org (not on initial load)
        const evictedOrgId = lastCrmOrgIdRef.current;
        clearCrmObjectStore();
        resetStaleMappingRecoveries();
        // The store is now empty, so any cached list entry (ids-only —
        // bodies live in the store) is a sentinel pointing at nothing, and
        // detail/slideout entries (staleTime: Infinity, never rewritten by
        // WS events — applyEvent.ts is store-only for these, see its module
        // doc comment) would otherwise survive as a status-only sentinel
        // whose store body is gone, permanently stuck loading. Evict the
        // unified list-key family plus the detail/slideout families for the
        // evicted org so a re-visit can't serve a cached sentinel with no
        // store data behind it.
        queryClient.removeQueries({
          predicate: query => {
            const key = query.queryKey;
            if (isCrmListKey(key)) {
              return true;
            }
            if (
              !Array.isArray(key) ||
              key[0] !== 'crm' ||
              key[1] !== evictedOrgId
            ) {
              return false;
            }
            // Detail family: ['crm', orgId, 'objects', entityType, 'detail', ...]
            if (key[2] === 'objects' && key[4] === 'detail') {
              return true;
            }
            // Slideout family: ['crm', orgId, 'object', objectId]
            return key[2] === 'object';
          },
        });
      }
      lastCrmOrgIdRef.current = newOrgId;
    }
  }, [currentOrg?.id, queryClient]);

  // Keep the query cache in sync with store LRU eviction: when the store
  // evicts a body, remove its detail/slideout cache entries (staleTime:
  // Infinity, never rewritten by WS events — applyEvent.ts is store-only for
  // these) and invalidate any cached list sentinel whose ids reference it, so
  // a list re-render triggers a refetch instead of rendering a missing row.
  useEffect(() => {
    const orgId = currentOrg?.id ?? null;
    if (orgId === null) {
      return;
    }
    const unsubscribe = subscribeToEviction(evicted => {
      try {
        if (evicted.length === 0) {
          return;
        }
        for (const rec of evicted) {
          queryClient.removeQueries({
            queryKey: crmQueryKeys.objectDetail(
              orgId,
              rec.entityTypeName,
              rec.entityId
            ),
          });
          queryClient.removeQueries({
            queryKey: ['crm', orgId, 'object', rec.objectId],
          });
        }
        invalidateListSentinelsForEvictedIds(
          queryClient,
          evicted.map(rec => rec.entityId)
        );
      } catch (err) {
        // A coupling failure must never break the store write that triggered eviction.
        logger.warn('[OrgContext] eviction-cache coupling failed', {
          error: err,
        });
      }
    });
    return unsubscribe;
  }, [queryClient, currentOrg?.id]);

  // Persist multi-org flag when user.id and availableOrgs are both available
  // This runs separately from loadTeams to handle cases where user.id becomes available after teams load
  useEffect(() => {
    if (user?.id && !isLoading && hasLoadedSuccessfullyRef.current) {
      updateMultiOrgFlag(user.id, availableOrgs.length > 1);
    }
  }, [user?.id, availableOrgs.length, isLoading]);

  // If teams loaded before user.id was known, dual-write once user.id arrives.
  // We persist even empty arrays so a user who has been removed from every
  // org doesn't see a stale persisted org list on next boot.
  useEffect(() => {
    if (!user?.id) {
      return;
    }
    if (!hasLoadedSuccessfullyRef.current) {
      return;
    }
    queryClient.setQueryData(['availableOrgs', user.id], availableOrgs);
    setPersistedAvailableOrgs(user.id, availableOrgs);
  }, [user?.id, availableOrgs, queryClient]);

  const value: OrgContextType = useMemo(
    () => ({
      currentOrg,
      availableOrgs,
      isLoading,
      error,
      hasLoadedSuccessfully,
      setCurrentTeam,
      switchOrg,
      refreshCurrentOrg,
      updateOrgFields,
    }),
    [
      currentOrg,
      availableOrgs,
      isLoading,
      error,
      hasLoadedSuccessfully,
      setCurrentTeam,
      switchOrg,
      refreshCurrentOrg,
      updateOrgFields,
    ]
  );

  return (
    <OrgContext value={value}>
      <CrmSubscriptionProvider
        subscribe={wsSubscribe}
        unsubscribe={wsUnsubscribe}
      >
        {children}
        <SingleObjectDeleteJobHost />
      </CrmSubscriptionProvider>
    </OrgContext>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within a TeamProvider');
  }
  return context;
}
